import { type QueryClient, type QueryKey, hashKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { formatISO } from "date-fns";
import { useTranslation } from "react-i18next";

import { toNotificationApiParams } from "./fetchNotifications";
import { notificationQueryKeys } from "./query-keys";
import {
  NOTIFICATIONS_ENDPOINT,
  NOTIFICATION_FILTER_PARAMS,
  NOTIFICATION_REJECTION_CODES,
  type NotificationAction,
} from "../constants";
import { notificationFiltersSchema, notificationProblemSchema } from "../schemas";
import type {
  NotificationActionTarget,
  NotificationData,
  NotificationFilters,
  NotificationsInfiniteData,
} from "../types";
import {
  applyActionToNotification,
  applyNotificationAction,
  flattenNotificationPages,
  getUnreadCountDelta,
} from "../utils";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";

type NotificationActionVariables = {
  action: NotificationAction;
  target: NotificationActionTarget;
  keepInView?: NotificationFilters;
};

type NotificationActionSnapshot = {
  pages: Array<[QueryKey, NotificationsInfiniteData | undefined]>;
  details: Array<[QueryKey, NotificationData | null | undefined]>;
  unreadCount: number | undefined;
};

function toTargetBody(target: NotificationActionTarget) {
  if ("notificationIds" in target) return { notificationIds: target.notificationIds };
  return {
    filter: {
      ...toNotificationApiParams(target.filter),
      [NOTIFICATION_FILTER_PARAMS.CREATED_AT_OR_BEFORE]: target.createdAtOrBefore,
    },
    ...(target.excludedIds.length > 0 && { excludedNotificationIds: target.excludedIds }),
  };
}

function getTransformedPageKey(target: NotificationActionTarget) {
  if ("notificationIds" in target) return undefined;
  return hashKey(notificationQueryKeys.page(target.filter));
}

function isRejectedAction(error: Error) {
  if (!isAxiosError(error)) return false;
  const problem = notificationProblemSchema.safeParse(error.response?.data);
  return problem.success && NOTIFICATION_REJECTION_CODES.has(problem.data.title);
}

function getCachedViews(queryClient: QueryClient) {
  return queryClient
    .getQueriesData<NotificationsInfiniteData>({ queryKey: notificationQueryKeys.pages() })
    .flatMap(([queryKey, data]) => {
      const filters = notificationFiltersSchema.safeParse(queryKey.at(-1));
      return filters.success ? [{ queryKey, status: filters.data.status, data }] : [];
    });
}

export function useNotificationActionMutation() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<void, Error, NotificationActionVariables, NotificationActionSnapshot>({
    mutationFn: async ({ action, target }) => {
      await instance.post(NOTIFICATIONS_ENDPOINT, { action, ...toTargetBody(target) });
    },
    onMutate: async ({ action, target, keepInView }) => {
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.pages() });
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.details() });
      await queryClient.cancelQueries({ queryKey: notificationQueryKeys.unreadCount() });

      const snapshot: NotificationActionSnapshot = {
        pages: queryClient.getQueriesData<NotificationsInfiniteData>({ queryKey: notificationQueryKeys.pages() }),
        details: queryClient.getQueriesData<NotificationData | null>({ queryKey: notificationQueryKeys.details() }),
        unreadCount: queryClient.getQueryData<number>(notificationQueryKeys.unreadCount()),
      };
      const views = getCachedViews(queryClient);
      const transformedPageKey = getTransformedPageKey(target);
      const keptPageKey = keepInView && hashKey(notificationQueryKeys.page(keepInView));
      const actedAt = formatISO(new Date());

      for (const view of views) {
        if (transformedPageKey && hashKey(view.queryKey) !== transformedPageKey) continue;
        queryClient.setQueryData<NotificationsInfiniteData>(
          view.queryKey,
          applyNotificationAction(view.data, view.status, action, target, actedAt, hashKey(view.queryKey) === keptPageKey),
        );
      }

      if ("notificationIds" in target) {
        for (const notificationId of target.notificationIds) {
          queryClient.setQueryData<NotificationData | null>(notificationQueryKeys.detail(notificationId), (detail) =>
            detail ? applyActionToNotification(detail, action, actedAt) : detail,
          );
        }
        const cachedNotifications = [
          ...views.flatMap((view) => flattenNotificationPages(view.data?.pages)),
          ...snapshot.details.flatMap(([, detail]) => (detail ? [detail] : [])),
        ];
        const delta = getUnreadCountDelta(cachedNotifications, action, target.notificationIds, actedAt);
        queryClient.setQueryData<number>(notificationQueryKeys.unreadCount(), (count) =>
          count === undefined ? count : Math.max(0, count + delta),
        );
      }

      return snapshot;
    },
    onError: (error, _variables, snapshot) => {
      for (const [queryKey, data] of [...(snapshot?.pages ?? []), ...(snapshot?.details ?? [])]) {
        queryClient.setQueryData(queryKey, data);
      }
      queryClient.setQueryData(notificationQueryKeys.unreadCount(), snapshot?.unreadCount);
      if (isRejectedAction(error)) {
        showToast(t("notifications.actionRejected"), "warning");
        return;
      }
      showToast(t("notifications.actionFailed"), "error");
    },
    onSettled: async (_data, _error, { keepInView }) => {
      if (!keepInView) {
        await queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationQueryKeys.pages(), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: notificationQueryKeys.filters(), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: notificationQueryKeys.details() }),
        queryClient.invalidateQueries({ queryKey: notificationQueryKeys.unreadCount() }),
      ]);
    },
  });
}
